/**
 * The one money model shape an authoring UI may produce: a plain amount, per
 * year, in the organisation's currency.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS RATHER THAN A FORM BUILDING ITS OWN FORMULA
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `person-hours-per-year.formula.ts` states the argument for the hours side and
 * every word of it applies here. `evaluateCoverageMoneyTerm` gates a money term
 * on three things in order — it must parse, it must reduce to `MONEY`, and its
 * period basis must be admissible and must match whatever basis this Role's
 * FIRST money term established. A screen that chooses its own dimension or its
 * own window is a screen that can author `IMPACT_MONEY_WRONG_DIMENSION` or
 * `IMPACT_MONEY_WRONG_PERIOD_BASIS`, which are honest states rendered as prose
 * and states a form must be incapable of producing.
 *
 * So the authoring surface collects ONE number and this function fixes the
 * dimension and the window. The guarantee is the builder's, not a check here:
 * `b.input` types the term from the dimension it is handed, so changing
 * `MONEY` below stops this file compiling rather than starting it emitting a
 * model the engine refuses at runtime.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 `perPeriod: "year"` IS LOAD-BEARING, AND OMITTING IT IS THE TEMPTING BUG
 * ══════════════════════════════════════════════════════════════════════════
 *
 * There are exactly two windows a money term may hold — `ABSOLUTE` (0) and
 * `ANNUAL` (-1) — both admissible on their own, and a Role's FIRST money term
 * fixes the window every later one is measured against. Omitting `perPeriod`
 * types this `ABSOLUTE`, which reads like the simpler choice and is the wrong
 * one:
 *
 * **Every money model that already exists declares a period.** Measured
 * read-only on staging 2026-08-13: all 4 stored workload money models and all
 * 16 stored impact money models carry `perPeriod`, so all 20 are `ANNUAL`. An
 * `ABSOLUTE` model written beside any of them is refused with
 * `WORKLOAD_MONEY_WRONG_PERIOD_BASIS` / `IMPACT_MONEY_WRONG_PERIOD_BASIS` — and
 * it is refused at READ time, so the write succeeds, the row is stored, and the
 * figure that was supposed to appear is replaced by prose on a screen the
 * author has already left.
 *
 * The annualisation is value-preserving at `year` and only there: a magnitude
 * stated per YEAR scales by one, where `month` would multiply by twelve. That
 * is why the field this collects is an ANNUAL amount and why there is no unit
 * picker beside it — the same argument the hours side makes.
 *
 * One shape for every money term this product writes is what makes the window
 * impossible to get wrong. A second shape is a second window waiting to meet
 * the first, and the last case in the spec is that meeting.
 *
 * ── ZERO AND NEGATIVE ARE NOT REFUSED HERE ─────────────────────────────────
 *
 * Same split as the hours side. `resolveCoverageBlendedRate` already names a
 * negative labour cost (`NEGATIVE_WORKLOAD_COST`) and deliberately lets a zero
 * through — "a Role that costs nothing prices every saving at nothing" is a
 * true answer. Refusing either here would be a second opinion about what a
 * legal amount is, and two opinions drift. The FORM refuses what it refuses,
 * which is where a reader can be told why.
 */
import { CoverageDimensions } from "./coverage-dimension.types";
import { buildCoverageFormula } from "./coverage-formula.builder";
import type { ValidatedCoverageFormula } from "./coverage-formula.schema";
import { COVERAGE_PERIOD_BASIS_ANNUAL } from "./coverage-period.types";

/** The one magnitude, and the word that goes beside it in the assumptions table. */
export interface AnnualMoneyFactors {
  /** The amount, per year, in the organisation's stated currency. */
  readonly amount: number;
  /** The input's stable key. Two money models on one row must not collide. */
  readonly key: string;
  /**
   * What the row is called wherever the stored model is printed.
   *
   * Supplied by the caller because only the caller has a locale, and stored
   * verbatim for the reason `person-hours-per-year.formula.ts` gives: a model
   * authored in French keeps its French row label.
   */
  readonly label: string;
}

/**
 * One authored amount, as a validated model whose dimension is `MONEY` and
 * whose period basis is `ANNUAL` by construction.
 *
 * The model has no steps: its `result` names the single input directly, which
 * `CoverageFormulaSchema` permits and `assertRefResolves` checks. A one-input
 * model is the honest shape for a figure somebody typed — wrapping it in an
 * `add` of one term would invent arithmetic the author did not do.
 *
 * @throws {CoverageFormulaBuilderError} only on a bug in the builder itself —
 * the input is a plain finite number and the shape is fixed.
 */
export function annualMoneyFormula(
  factors: AnnualMoneyFactors
): ValidatedCoverageFormula<typeof COVERAGE_PERIOD_BASIS_ANNUAL> {
  return buildCoverageFormula((b) =>
    b.input({
      key: factors.key,
      value: factors.amount,
      dimension: CoverageDimensions.MONEY,
      perPeriod: "year",
      label: factors.label
    })
  );
}
